import React from "react";
import { observer } from "mobx-react";
import { EvaluationQueryUpdateStore } from "../../store";
import { EvaluationQueryUpdateForm } from "./EvaluationQueryUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { EvaluationPathType } from "../../../../api/graphql";
import { EvaluationDifficulty } from "../../../../api/graphql";
import { EvaluationAmbiguityType } from "../../../../api/graphql";

export type EvaluationQueryUpdateProps = {
  evaluationQueryUpdateStore: EvaluationQueryUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const EvaluationQueryUpdate: React.FC<EvaluationQueryUpdateProps> =
  observer(
    ({ evaluationQueryUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={evaluationQueryUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={evaluationQueryUpdateStore.savedButOutdated}
            onReload={() => evaluationQueryUpdateStore.reloadEvaluationQuery()}
            reloadDisabled={evaluationQueryUpdateStore.isProcessing}
          />
          <EvaluationQueryUpdateForm
            evaluationQueryUpdateStore={evaluationQueryUpdateStore}
            queryError={evaluationQueryUpdateStore.errors.query?.message}
            onQueryChange={(value: string) => {
              evaluationQueryUpdateStore.setQueryValue(value);
            }}
            query={evaluationQueryUpdateStore.entityValues.query}
            pathTypeError={evaluationQueryUpdateStore.errors.pathType?.message}
            pathTypeOptions={evaluationQueryUpdateStore.getPathTypeOptions()}
            onPathTypeChange={(value: EvaluationPathType) => {
              evaluationQueryUpdateStore.setPathTypeValue(value);
            }}
            pathType={evaluationQueryUpdateStore.entityValues.pathType}
            difficultyError={
              evaluationQueryUpdateStore.errors.difficulty?.message
            }
            difficultyOptions={evaluationQueryUpdateStore.getDifficultyOptions()}
            onDifficultyChange={(value: EvaluationDifficulty) => {
              evaluationQueryUpdateStore.setDifficultyValue(value);
            }}
            difficulty={evaluationQueryUpdateStore.entityValues.difficulty}
            expectedToolError={
              evaluationQueryUpdateStore.errors.expectedTool?.message
            }
            onExpectedToolChange={(value: string) => {
              evaluationQueryUpdateStore.setExpectedToolValue(value);
            }}
            expectedTool={evaluationQueryUpdateStore.entityValues.expectedTool}
            expectedArgsError={
              evaluationQueryUpdateStore.errors.expectedArgs?.message
            }
            onExpectedArgsChange={(value: string) => {
              evaluationQueryUpdateStore.setExpectedArgsValue(value);
            }}
            expectedArgs={evaluationQueryUpdateStore.entityValues.expectedArgs}
            acceptableToolsError={
              evaluationQueryUpdateStore.errors.acceptableTools?.message
            }
            onAcceptableToolsChange={(value: string) => {
              evaluationQueryUpdateStore.setAcceptableToolsValue(value);
            }}
            acceptableTools={
              evaluationQueryUpdateStore.entityValues.acceptableTools
            }
            expectedResponsePatternError={
              evaluationQueryUpdateStore.errors.expectedResponsePattern?.message
            }
            onExpectedResponsePatternChange={(value: string) => {
              evaluationQueryUpdateStore.setExpectedResponsePatternValue(value);
            }}
            expectedResponsePattern={
              evaluationQueryUpdateStore.entityValues.expectedResponsePattern
            }
            generatedFromError={
              evaluationQueryUpdateStore.errors.generatedFrom?.message
            }
            onGeneratedFromChange={(value: string) => {
              evaluationQueryUpdateStore.setGeneratedFromValue(value);
            }}
            generatedFrom={
              evaluationQueryUpdateStore.entityValues.generatedFrom
            }
            isAmbiguousError={
              evaluationQueryUpdateStore.errors.isAmbiguous?.message
            }
            onIsAmbiguousChange={(value: boolean) => {
              evaluationQueryUpdateStore.setIsAmbiguousValue(value);
            }}
            isAmbiguous={evaluationQueryUpdateStore.entityValues.isAmbiguous}
            ambiguityTypeError={
              evaluationQueryUpdateStore.errors.ambiguityType?.message
            }
            ambiguityTypeOptions={evaluationQueryUpdateStore.getAmbiguityTypeOptions()}
            onAmbiguityTypeChange={(value: EvaluationAmbiguityType | "") => {
              evaluationQueryUpdateStore.setAmbiguityTypeValue(value);
            }}
            ambiguityType={
              evaluationQueryUpdateStore.entityValues.ambiguityType
            }
            disambiguationResponseError={
              evaluationQueryUpdateStore.errors.disambiguationResponse?.message
            }
            onDisambiguationResponseChange={(value: string) => {
              evaluationQueryUpdateStore.setDisambiguationResponseValue(value);
            }}
            disambiguationResponse={
              evaluationQueryUpdateStore.entityValues.disambiguationResponse
            }
            notesError={evaluationQueryUpdateStore.errors.notes?.message}
            onNotesChange={(value: string) => {
              evaluationQueryUpdateStore.setNotesValue(value);
            }}
            notes={evaluationQueryUpdateStore.entityValues.notes}
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={evaluationQueryUpdateStore.saveDisabled}
            onSave={() => evaluationQueryUpdateStore.saveEvaluationQuery()}
            onCancel={() => evaluationQueryUpdateStore.cancelEvaluationQuery()}
          />
          <ErrorDialog
            open={evaluationQueryUpdateStore.errorDialogOpened}
            title={evaluationQueryUpdateStore.errorTitle}
            description={evaluationQueryUpdateStore.errorDescription}
            onClose={() => evaluationQueryUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={evaluationQueryUpdateStore.confirmDialogOpened}
            title={evaluationQueryUpdateStore.confirmDialogTitle}
            description={evaluationQueryUpdateStore.confirmDialogDescription}
            onConfirm={() =>
              evaluationQueryUpdateStore.executeConfirmedAction()
            }
            onCancel={() => evaluationQueryUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
